import React, { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Copy, RefreshCw, Sparkles } from 'lucide-react';
import {
  getMyLiveEffect,
  getMyLiveEffectTriggers,
  LiveEffectItem,
  LiveEffectMine,
  LiveEffectTriggerRow,
  regenerateLiveEffectKey,
  removeLiveBillboard,
  resetLiveEffectObs,
  saveLiveEffect,
} from '../../../api/payment/live-effect.api';
import {
  billboardStatusLabel,
  EFFECT_ICONS,
  formatCa,
  formatClock,
  formatSecondsLeft,
  POSITION_LABELS,
  TRIGGER_NOTE_LABEL,
  TRIGGER_STATUS_LABEL,
  validateLiveEffectForm,
} from '../../../utils/live-effect';

/**
 * Khối "Hiệu ứng tương tác" trong Cài đặt Donate tab Tương tác: streamer bật trò, đặt giá, thời lượng, thời gian chờ,
 * khai tên nguồn OBS cho nhóm trò mạnh, mở gói thầu khung tên, xem lịch sử gói thầu và lượt bấm gần đây.
 */

const errorMessage = (
  err: unknown,
  fallback = 'Không lưu được, thử lại sau.'
) =>
  (err as { response?: { data?: { message?: string } } })?.response?.data
    ?.message || fallback;

const inputCls =
  'w-full rounded-md border ui-divider bg-transparent px-2 py-1 text-[13px] outline-none ui-text';

/** Ô trống hoặc chữ không phải số thành NaN; số lẻ giữ nguyên. Cả hai bị báo lúc lưu, không tự làm tròn. */
const parseDraft = (raw: string) => (raw.trim() === '' ? NaN : Number(raw));

/**
 * Ô số giữ chữ đang gõ (cho phép xoá trắng rồi gõ lại), báo lên cha giá trị đã đọc. Cha đổi giá trị từ ngoài
 * (máy chủ trả về sau khi lưu) thì ô hiện theo giá trị mới.
 */
const NumberInput: React.FC<{
  label: string;
  value: number;
  min?: number;
  max?: number;
  onChange: (next: number) => void;
}> = ({ label, value, min, max, onChange }) => {
  const [text, setText] = useState(Number.isFinite(value) ? String(value) : '');
  useEffect(() => {
    setText((prev) =>
      Object.is(parseDraft(prev), value)
        ? prev
        : Number.isFinite(value)
        ? String(value)
        : ''
    );
  }, [value]);
  return (
    <input
      aria-label={label}
      type="number"
      inputMode="numeric"
      min={min}
      max={max}
      className={inputCls}
      value={text}
      onChange={(e) => {
        setText(e.target.value);
        onChange(parseDraft(e.target.value));
      }}
    />
  );
};

// Khai ngoài component cha để không remount (mất chữ đang gõ) mỗi lần cha render lại.
const EffectRow: React.FC<{
  item: LiveEffectItem;
  minPriceCa: number;
  onChange: (next: LiveEffectItem) => void;
}> = ({ item, minPriceCa, onChange }) => (
  <div
    data-effect-row
    className="grid grid-cols-2 items-center gap-2 border-b ui-divider py-2 sm:grid-cols-[1.4fr_1fr_1fr_1fr]"
  >
    <label className="col-span-2 flex items-center gap-2 text-[14px] ui-text sm:col-span-1">
      <input
        type="checkbox"
        checked={item.enabled}
        onChange={(e) => onChange({ ...item, enabled: e.target.checked })}
      />
      <span>
        {EFFECT_ICONS[item.code]}
        <span className="ml-1">{item.name}</span>
      </span>
    </label>
    <label className="text-[12px] ui-muted">
      Giá (Ca)
      <NumberInput
        label="Giá (Ca)"
        min={Math.max(1, minPriceCa || 0)}
        value={item.priceCa}
        onChange={(priceCa) => onChange({ ...item, priceCa })}
      />
    </label>
    <label className="text-[12px] ui-muted">
      Thời lượng (giây)
      <NumberInput
        label="Thời lượng (giây)"
        min={3}
        max={15}
        value={item.durationSec}
        onChange={(durationSec) => onChange({ ...item, durationSec })}
      />
    </label>
    <label className="text-[12px] ui-muted">
      Chờ bấm lại (giây)
      <NumberInput
        label="Chờ bấm lại (giây)"
        min={0}
        max={3600}
        value={item.cooldownSec}
        onChange={(cooldownSec) => onChange({ ...item, cooldownSec })}
      />
    </label>
    <label className="col-span-2 flex items-center gap-2 text-[12px] ui-muted sm:col-span-4">
      <input
        type="checkbox"
        aria-label="Chỉ hội viên"
        checked={!!item.membersOnly}
        onChange={(e) => onChange({ ...item, membersOnly: e.target.checked })}
      />
      Chỉ hội viên
    </label>
  </div>
);

const LiveEffectSetting: React.FC<{ userId?: string }> = ({ userId }) => {
  const [mine, setMine] = useState<LiveEffectMine | null>(null);
  const [triggers, setTriggers] = useState<LiveEffectTriggerRow[]>([]);
  const [saving, setSaving] = useState(false);
  const [askReason, setAskReason] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [formError, setFormError] = useState('');

  const load = useCallback(() => {
    setLoadFailed(false);
    getMyLiveEffect()
      .then(setMine)
      .catch(() => setLoadFailed(true));
    getMyLiveEffectTriggers()
      .then(setTriggers)
      .catch(() => setTriggers([]));
  }, []);
  useEffect(load, [load]);

  if (!mine) {
    if (!loadFailed) return null;
    return (
      <div className="mt-6 box rounded-2xl p-4 md:p-6 text-[14px] ui-text">
        <p>Không tải được cài đặt hiệu ứng.</p>
        <button
          type="button"
          className="mt-2 rounded-md border ui-divider px-3 py-1 text-[13px] ui-text"
          onClick={load}
        >
          Thử lại
        </button>
      </div>
    );
  }

  const overlayUrl =
    mine.overlayKey && userId
      ? `${window.location.origin}/effect-screen/${userId}?key=${mine.overlayKey}`
      : '';

  const patch = (p: Partial<LiveEffectMine>) => {
    setFormError('');
    setMine({ ...mine, ...p });
  };
  const setEffect = (next: LiveEffectItem) =>
    patch({
      effects: mine.effects.map((e) => (e.code === next.code ? next : e)),
    });

  const save = () => {
    if (saving) return;
    const invalid = validateLiveEffectForm(mine);
    setFormError(invalid ?? '');
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSaving(true);
    saveLiveEffect({
      isEnabled: mine.isEnabled,
      isPaused: mine.isPaused,
      effects: mine.effects.map(
        ({ code, enabled, priceCa, durationSec, cooldownSec, membersOnly }) => ({
          code,
          enabled,
          priceCa,
          durationSec,
          cooldownSec,
          membersOnly: !!membersOnly,
        })
      ),
      obsSourceName: mine.obsSourceName,
      obsMicName: mine.obsMicName,
      obsPenaltySceneName: mine.obsPenaltySceneName,
      billboardEnabled: mine.billboardEnabled,
      billboardPosition: mine.billboardPosition,
      billboardPackages: mine.billboardPackages,
    })
      .then((res) => {
        setMine(res);
        toast.success('Đã lưu cài đặt hiệu ứng.');
      })
      .catch((err) => toast.error(errorMessage(err)))
      .finally(() => setSaving(false));
  };

  const overlayGroup = mine.effects.filter((e) => e.group === 'Overlay');
  const historyBillboards = (mine.recentBillboards ?? []).filter(
    (b) => b.status !== 'Failed' && b.status !== 'Charging'
  );
  const obsGroup = mine.effects.filter((e) => e.group === 'Obs');

  return (
    <div className="mt-6 box rounded-2xl p-4 md:p-6">
      <div className="flex items-center gap-2">
        <Sparkles className="text-blue-600" size={20} />
        <h3 className="text-lg font-semibold ui-heading">Hiệu ứng tương tác</h3>
      </div>
      <p className="mt-1 text-[13px] ui-muted">
        Người xem trả Ca để bắn hiệu ứng lên màn hình live của bạn. Bạn chọn trò
        nào được dùng, giá bao nhiêu, kéo dài mấy giây và bao lâu mới được bấm
        lại.
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-4 text-[14px] ui-text">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={mine.isEnabled}
            onChange={(e) => patch({ isEnabled: e.target.checked })}
          />{' '}
          Bật hiệu ứng tương tác
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={mine.isPaused}
            onChange={(e) => patch({ isPaused: e.target.checked })}
          />{' '}
          Tạm dừng nhận lượt mới
        </label>
      </div>

      <div className="mt-3 flex flex-col gap-1 text-[13px]">
        <span className={mine.overlayOnline ? 'text-emerald-600' : 'ui-muted'}>
          {mine.overlayOnline
            ? 'Màn hình hiệu ứng: đang chạy'
            : 'Màn hình hiệu ứng: chưa mở'}
        </span>
        <span className={mine.obsOnline ? 'text-emerald-600' : 'ui-muted'}>
          {mine.obsOnline ? 'OBS: đã kết nối' : 'OBS: chưa kết nối'}
        </span>
      </div>

      {overlayUrl && (
        <div className="mt-3 flex min-w-0 items-center gap-2">
          <input
            readOnly
            className={`${inputCls} min-w-0`}
            value={overlayUrl}
          />
          <button
            type="button"
            className="shrink-0 rounded-md border ui-divider p-2 ui-text"
            aria-label="Sao chép link"
            onClick={() =>
              Promise.resolve()
                .then(() => navigator.clipboard.writeText(overlayUrl))
                .then(() => toast.success('Đã sao chép link.'))
                .catch(() =>
                  toast.error(
                    'Không sao chép được, hãy chọn và sao chép link thủ công.'
                  )
                )
            }
          >
            <Copy size={16} />
          </button>
          <button
            type="button"
            className="shrink-0 rounded-md border ui-divider p-2 ui-text"
            aria-label="Đổi khoá"
            onClick={() =>
              regenerateLiveEffectKey()
                .then((res) => {
                  setMine((prev) =>
                    prev ? { ...prev, overlayKey: res.overlayKey } : prev
                  );
                  toast.success('Đã đổi khoá, link cũ ngưng hoạt động.');
                })
                .catch((err) => toast.error(errorMessage(err)))
            }
          >
            <RefreshCw size={16} />
          </button>
        </div>
      )}
      <p className="mt-1 text-[12px] ui-muted">
        Thêm link này vào OBS thành nguồn trình duyệt, phủ toàn màn hình, đặt
        trên cùng.
      </p>

      <h4 className="mt-5 font-semibold ui-heading">Nhóm thường</h4>
      {overlayGroup.map((e) => (
        <EffectRow
          key={e.code}
          item={e}
          minPriceCa={mine.minPriceCa}
          onChange={setEffect}
        />
      ))}

      <h4 className="mt-5 font-semibold ui-heading">
        Nhóm mạnh, cần kết nối OBS
      </h4>
      <ol className="mt-1 list-decimal pl-5 text-[12px] ui-muted">
        <li>
          Trong OBS mở menu Tools, chọn WebSocket Server Settings, bật Enable
          WebSocket server và đặt mật khẩu. Mật khẩu chỉ nên gồm chữ cái không
          dấu và chữ số.
        </li>
        <li>
          Thêm vào cuối link overlay đoạn <code>#obs=MẬT_KHẨU</code>. Nếu OBS
          không dùng cổng mặc định 4455, thêm <code>&amp;port=SỐ_CỔNG</code>{' '}
          ngay sau mật khẩu. Mật khẩu chỉ nằm trên máy bạn, không gửi về Cab.
        </li>
        <li>Gõ đúng tên nguồn camera hoặc game trong OBS vào ô bên dưới.</li>
      </ol>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <label className="text-[12px] ui-muted">
          Tên nguồn chính
          <input
            aria-label="Tên nguồn chính"
            className={inputCls}
            value={mine.obsSourceName ?? ''}
            onChange={(e) => patch({ obsSourceName: e.target.value })}
          />
        </label>
        <label className="text-[12px] ui-muted">
          Tên mic (cho trò Câm lặng)
          <input
            aria-label="Tên mic"
            className={inputCls}
            value={mine.obsMicName ?? ''}
            onChange={(e) => patch({ obsMicName: e.target.value })}
          />
        </label>
        <label className="text-[12px] ui-muted">
          Tên cảnh phạt
          <input
            aria-label="Tên cảnh phạt"
            className={inputCls}
            value={mine.obsPenaltySceneName ?? ''}
            onChange={(e) => patch({ obsPenaltySceneName: e.target.value })}
          />
        </label>
      </div>
      {obsGroup.map((e) => (
        <EffectRow
          key={e.code}
          item={e}
          minPriceCa={mine.minPriceCa}
          onChange={setEffect}
        />
      ))}
      <button
        type="button"
        className="mt-2 rounded-md border ui-divider px-3 py-1 text-[13px] ui-text"
        onClick={() =>
          resetLiveEffectObs()
            .then(() => toast.success('Đã gửi lệnh trả hình về như cũ.'))
            .catch((err) => toast.error(errorMessage(err)))
        }
      >
        Trả hình về như cũ
      </button>

      <h4 className="mt-5 font-semibold ui-heading">Gói thầu khung tên</h4>
      <label className="mt-1 flex items-center gap-2 text-[14px] ui-text">
        <input
          type="checkbox"
          checked={mine.billboardEnabled}
          onChange={(e) => patch({ billboardEnabled: e.target.checked })}
        />{' '}
        Cho người xem thầu khung tên
      </label>
      <label className="mt-2 block text-[12px] ui-muted">
        Vị trí khung
        <select
          className={inputCls}
          value={mine.billboardPosition}
          onChange={(e) => patch({ billboardPosition: e.target.value })}
        >
          {Object.entries(POSITION_LABELS).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
      </label>
      {mine.billboardPackages.map((p, i) => (
        <div key={i} className="mt-2 flex items-end gap-2">
          <label className="text-[12px] ui-muted">
            Số giờ
            <NumberInput
              label={`Số giờ gói ${i + 1}`}
              min={1}
              max={24}
              value={p.hours}
              onChange={(h) =>
                patch({
                  billboardPackages: mine.billboardPackages.map((x, j) =>
                    j === i ? { ...x, hours: h } : x
                  ),
                })
              }
            />
          </label>
          <label className="text-[12px] ui-muted">
            Giá (Ca)
            <NumberInput
              label={`Giá gói ${i + 1} (Ca)`}
              min={Math.max(1, mine.minPriceCa || 0)}
              value={p.priceCa}
              onChange={(price) =>
                patch({
                  billboardPackages: mine.billboardPackages.map((x, j) =>
                    j === i ? { ...x, priceCa: price } : x
                  ),
                })
              }
            />
          </label>
          {mine.billboardPackages.length > 1 && (
            <button
              type="button"
              className="rounded-md border ui-divider px-2 py-1 text-[12px] ui-text"
              onClick={() =>
                patch({
                  billboardPackages: mine.billboardPackages.filter(
                    (_, j) => j !== i
                  ),
                })
              }
            >
              Bỏ gói
            </button>
          )}
        </div>
      ))}
      {mine.billboardPackages.length < 4 && (
        <button
          type="button"
          className="mt-2 rounded-md border ui-divider px-3 py-1 text-[13px] ui-text"
          onClick={() =>
            patch({
              billboardPackages: [
                ...mine.billboardPackages,
                {
                  hours: Math.min(
                    24,
                    (mine.billboardPackages[mine.billboardPackages.length - 1]
                      ?.hours ?? 0) + 1
                  ),
                  priceCa: 100000,
                },
              ],
            })
          }
        >
          Thêm gói
        </button>
      )}

      <div className="mt-3 rounded-lg border ui-divider p-3 text-[13px] ui-text">
        {mine.activeBillboard ? (
          <>
            <p>
              📌 {mine.activeBillboard.viewerName} ·{' '}
              {mine.activeBillboard.message}
            </p>
            <p className="ui-muted">
              Còn {formatSecondsLeft(mine.activeBillboard.secondsLeft)} live
            </p>
            {!askReason ? (
              <button
                type="button"
                className="mt-2 rounded-md border border-red-300 px-3 py-1 text-red-600"
                onClick={() => setAskReason(true)}
              >
                Gỡ gói thầu
              </button>
            ) : (
              <div className="mt-2 flex flex-wrap gap-2">
                <span className="ui-muted">Lý do gỡ, không hoàn tiền:</span>
                {mine.removeReasons.map((r) => (
                  <button
                    key={r}
                    type="button"
                    className="rounded-md border ui-divider px-2 py-1"
                    onClick={() =>
                      removeLiveBillboard(r)
                        .then((res) => {
                          setMine((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  activeBillboard: res.activeBillboard,
                                  recentBillboards: res.recentBillboards,
                                }
                              : prev
                          );
                          setAskReason(false);
                          toast.success('Đã gỡ gói thầu.');
                        })
                        .catch((err) =>
                          toast.error(
                            errorMessage(
                              err,
                              'Không gỡ được gói thầu, thử lại sau.'
                            )
                          )
                        )
                    }
                  >
                    {r}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <p className="ui-muted">Chưa có ai thầu khung.</p>
        )}
      </div>

      {historyBillboards.length > 0 && (
        <>
          <h4 className="mt-4 text-[14px] font-semibold ui-heading">
            Lịch sử gói thầu
          </h4>
          <ul className="mt-1 divide-y ui-divider text-[13px] ui-text">
            {historyBillboards.map((b) => (
              <li
                key={b.id}
                data-billboard-row
                className="flex flex-wrap justify-between gap-2 py-1"
              >
                <span className="min-w-0 break-words">
                  {b.viewerName} · {b.message} · {b.hours} giờ
                </span>
                <span className="ui-muted">
                  {billboardStatusLabel(b.status, b.removedReason)}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}

      {formError && (
        <p role="alert" className="mt-4 text-[13px] text-red-600">
          {formError}
        </p>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={save}
        className="mt-4 rounded-lg bg-[#0057FF] px-4 py-2 text-[14px] font-semibold text-white disabled:opacity-60"
      >
        {saving ? 'Đang lưu…' : 'Lưu cài đặt'}
      </button>

      <h4 className="mt-6 font-semibold ui-heading">Lượt bấm gần đây</h4>
      {triggers.length === 0 ? (
        <p className="text-[13px] ui-muted">Chưa có lượt bấm nào.</p>
      ) : (
        <ul className="mt-1 divide-y ui-divider text-[13px] ui-text">
          {triggers.map((t) => (
            <li
              key={t.id}
              className="flex flex-wrap justify-between gap-2 py-1"
            >
              <span>
                <span className="ui-muted">{formatClock(t.createdAt)}</span>{' '}
                {t.viewerName} · {EFFECT_ICONS[t.code]} {t.name} ·{' '}
                {formatCa(t.priceCa)}
              </span>
              <span className="ui-muted">
                {TRIGGER_STATUS_LABEL[t.status] ?? t.status}
                {t.note ? TRIGGER_NOTE_LABEL[t.note] ?? '' : ''}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default LiveEffectSetting;
